import React from 'react';
import styled from 'styled-components';

/*
 * KeyValueTable — semantic <table> primitive for sheet sections.
 *
 * Hard contract:
 *  - Root element MUST be <table> (verifiable in DevTools)
 *  - Label cells use <th scope="row"> for screen-reader semantics
 *  - Active row gets a 3px left border + bold label (never color alone)
 *  - Sub-rows are one level deep, indented with no label repetition
 */

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.theme.typography.sizes.md};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const LabelCell = styled.th`
  text-align: left;
  vertical-align: top;
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  font-weight: ${p => p.active ? p.theme.typography.weights.bold : p.theme.typography.weights.regular};
  color: ${p => {
    if (p.active && p.accentTone === 'good') return p.theme.colors.accentTones.good;
    if (p.active && p.accentTone === 'bad') return p.theme.colors.accentTones.bad;
    return p.theme.colors.text.label;
  }};
  border-left: 3px solid ${p => {
    if (p.active && p.accentTone === 'good') return p.theme.colors.accentTones.good;
    if (p.active && p.accentTone === 'bad') return p.theme.colors.accentTones.bad;
    if (p.active) return p.theme.colors.text.label;
    return 'transparent';
  }};
  width: 40%;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    width: 45%;
    font-size: 14px;
  }

  @media print { color: #000; border-left-color: transparent; }
`;

const LabelTa = styled.span`
  display: block;
  font-size: 15px;
  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 14px;
  }
  @media print { color: #000; }
`;

const LabelEn = styled.span`
  display: block;
  font-size: 12px;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 2px;
  @media print { color: #000; }
`;

const ValueCell = styled.td`
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  vertical-align: top;
  color: ${p => {
    if (p.accentTone && p.accentTone !== 'neutral') {
      return p.theme.colors.accentTones[p.accentTone];
    }
    return p.theme.colors.text.primary;
  }};
  @media print { color: #000; }
`;

const EndTimeCell = styled.td`
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  text-align: right;
  vertical-align: top;
  font-family: ${p => p.theme.typography.monoFamily};
  color: ${p => {
    if (p.accentTone && p.accentTone !== 'neutral') {
      return p.theme.colors.accentTones[p.accentTone];
    }
    return p.theme.colors.text.secondary;
  }};
  white-space: nowrap;
  font-size: 15px;
  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 14px;
  }
  @media print { color: #000; }
`;

const SubRowLabelCell = styled.th`
  padding: ${p => p.theme.spacing[1]}px ${p => p.theme.spacing[3]}px ${p => p.theme.spacing[1]}px ${p => p.theme.spacing[6]}px;
  border-left: 3px solid transparent;
  font-weight: ${p => p.theme.typography.weights.regular};
  width: 40%;
`;

function renderRow(row, isActive, accentTone, lang, isSubRow = false) {
  const LabelTag = isSubRow ? SubRowLabelCell : LabelCell;
  return (
    <Tr key={row.key}>
      <LabelTag scope="row" active={isActive} accentTone={accentTone}>
        {!isSubRow && (
          <>
            <LabelTa>{lang === 'ta' ? row.labelTa : row.labelEn}</LabelTa>
            {lang === 'ta' && row.labelEn && <LabelEn>{row.labelEn}</LabelEn>}
            {lang !== 'ta' && row.labelTa && <LabelEn>{row.labelTa}</LabelEn>}
          </>
        )}
      </LabelTag>
      <ValueCell accentTone={accentTone}>{row.value}</ValueCell>
      <EndTimeCell accentTone={accentTone}>{row.endTime || ''}</EndTimeCell>
    </Tr>
  );
}

const KeyValueTable = ({ rows, activeRowKey = null, lang = 'en', accentTone }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <Table>
      <tbody>
        {rows.map(row => {
          const isActive = row.key === activeRowKey;
          const elements = [renderRow(row, isActive, accentTone, lang)];
          if (row.subRows && row.subRows.length > 0) {
            row.subRows.forEach(sub => {
              const subActive = sub.key === activeRowKey;
              elements.push(renderRow(sub, subActive, accentTone, lang, true));
            });
          }
          return <React.Fragment key={row.key}>{elements}</React.Fragment>;
        })}
      </tbody>
    </Table>
  );
};

export default KeyValueTable;
